<script setup>
import { MenuButtonGroup } from '@dashhudson/dashing-ui/menu-button-group';
import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import difference from 'lodash/difference';
import isNil from 'lodash/isNil';
import { computed } from 'vue';

import AssignmentMenu from '@/app/community/components/AssignmentMenu.vue';
import TagEditableMenu from '@/app/community/components/TagEditableMenu.vue';
import {
  COMMUNITY_INTERACTION_ACTION_BUTTON_LOCATION_LABELS,
  COMMUNITY_INTERACTION_ACTION_BUTTON_LOCATIONS,
  COMMUNITY_INTERACTION_ERROR_TOAST,
  COMMUNITY_INTERACTION_PLATFORM_BY_TYPE,
} from '@/app/community/constants';
import {
  ACTION_MENU_DISPLAY_LIMIT,
  ACTION_MENU_ITEMS,
} from '@/app/community/constants/interactionControl';
import { getToastForInteractionType } from '@/app/community/utils/community-interactions';
import {
  communityConversationUpdateEvent,
  getBrandHandleFromInteraction,
  messageAssignedEvent,
} from '@/app/community/utils/mixpanel-events';
import { useAuthStore } from '@/stores/auth';
import { useCommunityStore } from '@/stores/community';
import { useCommunityInteractionStore } from '@/stores/community-interactions';
import { logger } from '@/utils/logger';

const props = defineProps({
  interaction: { type: Object, required: true },
  vertical: { type: Boolean, default: undefined },
  collapsed: { type: Boolean, default: undefined },
  hiddenMenuItems: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: undefined },
  inPreview: { type: Boolean, default: undefined },
});

const interactionStore = useCommunityInteractionStore();
const communityStore = useCommunityStore();
const authStore = useAuthStore();

const { addToast } = useToast({ closable: true, life: 3000 });

const orientation = computed(() => (props.vertical ? 'vertical' : 'horizontal'));

async function onActionClicked(payload, action) {
  let updatedInteraction;
  try {
    updatedInteraction = await interactionStore.updateCommunityInteraction(
      props.interaction.id,
      payload,
      false,
      null,
      null,
      true,
    );
  } catch (error) {
    addToast({ message: COMMUNITY_INTERACTION_ERROR_TOAST, severity: 'error' });
    // eslint-disable-next-line no-console
    console.error(
      `Error occurred while updating interaction (id: ${props.interaction.id}, brand: ${props.interaction.brandId}, action: ${action}): ${error.message}`,
    );
    // eslint-disable-next-line no-console
    console.error(error);
    logger.error(
      `Failed to update community interaction (id: ${props.interaction.id}, brand:${props.interaction.brandId})`,
      {},
      error,
    );
    return;
  }

  addToast({
    message: getToastForInteractionType(props.interaction.type, action),
    severity: 'success',
  });

  const { platform = 'UNKNOWN', messageType = 'UNKNOWN' } =
    COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[props.interaction.type];
  communityConversationUpdateEvent({
    platform,
    messageType,
    action,
    location:
      COMMUNITY_INTERACTION_ACTION_BUTTON_LOCATION_LABELS[
        props.vertical
          ? COMMUNITY_INTERACTION_ACTION_BUTTON_LOCATIONS.PANEL
          : COMMUNITY_INTERACTION_ACTION_BUTTON_LOCATIONS.LIST
      ],
    originalCommunityInteraction: props.interaction,
    updatedCommunityInteraction: updatedInteraction?.data?.data,
  });
}

async function onAssignTags(newTagIds) {
  const oldTagIds = props.interaction.tagIds;
  const add = difference(newTagIds, oldTagIds);
  const remove = difference(oldTagIds, newTagIds);
  await onActionClicked({ tagIds: { add, remove } }, ACTION_MENU_ITEMS.TAG.action);
}

async function onAssignUser(newUserId) {
  const userId = newUserId === -1 ? null : newUserId;
  try {
    if (userId !== props.interaction.assigneeId) {
      const { successMessage } = await communityStore.createCommunityAssignment(
        {
          brandId: props.interaction.brandId,
          interactionId: props.interaction.id,
          assignmentPayload: { assignedToUser: userId },
        },
        false,
        false,
      );
      addToast({
        message: successMessage,
        severity: 'success',
      });
    }
  } catch {
    addToast({
      summary: 'Unable to assign message',
      message: 'Please try again',
      severity: 'error',
    });
  }
  messageAssignedEvent({
    messageType: props.interaction.type,
    interactionId: props.interaction.id,
    isAutomated: false,
    assignedById: authStore.identity.id,
    assignedToId: userId,
    isAdComment: props.interaction.isAdComment || false,
    messageBrandId: props.interaction.brandId,
    messageBrandName: getBrandHandleFromInteraction(props.interaction),
  });
}

const commandMenuItems = computed(() => {
  const items = [
    props.interaction.isArchived ? ACTION_MENU_ITEMS.UNARCHIVE : ACTION_MENU_ITEMS.ARCHIVE,
    props.interaction.isRead ? ACTION_MENU_ITEMS.UNREAD : ACTION_MENU_ITEMS.READ,
    props.interaction.isFollowUp ? ACTION_MENU_ITEMS.UNFOLLOWUP : ACTION_MENU_ITEMS.FOLLOWUP,
  ];
  return items.map((item) => ({
    ...item,
    command: (e) => {
      e?.stopPropagation();
      onActionClicked(item.payload, item.action);
    },
  }));
});

const tagDropdownId = computed(() => {
  const parts = ['community-tag'];
  if (props.inPreview) parts.push('preview');
  parts.push(props.interaction.id);
  return parts.join('-');
});

const assignmentDropdownId = computed(() => {
  const parts = ['community-assignment'];
  if (props.inPreview) parts.push('preview');
  parts.push(props.interaction.id);
  return parts.join('-');
});

const showAssigneeAvatar = computed(() => !isNil(props.interaction.assigneeId));

const assignmentDropdown = computed(() => {
  if (showAssigneeAvatar.value) {
    return { placement: 'bottom-start' };
  }
  return {
    targetNodes: `#${assignmentDropdownId.value}`,
    referenceNode: `#${assignmentDropdownId.value}`,
    placement: 'bottom-start',
  };
});

const nonStaticMenuItems = computed(() => {
  if (!showAssigneeAvatar.value) {
    return [
      {
        ...ACTION_MENU_ITEMS.ASSIGN,
        id: assignmentDropdownId.value,
        command: (e) => {
          e?.stopPropagation();
        },
      },
    ];
  }
  return [];
});

const menuItems = computed(() => [
  ...commandMenuItems.value,
  {
    ...ACTION_MENU_ITEMS.TAG,
    id: tagDropdownId.value,
    command: (e) => {
      e?.stopPropagation();
    },
  },
  ...nonStaticMenuItems.value,
]);
</script>

<template>
  <div :class="['dash-flex dash-items-center', { 'dash-flex-col dash-justify-center': vertical }]">
    <MenuButtonGroup
      key="interactionMainMenuButtonGroup"
      :model="menuItems"
      :display-limit="ACTION_MENU_DISPLAY_LIMIT"
      :orientation="orientation"
    />
    <TagEditableMenu
      v-show="false"
      :key="inPreview ? 'preview-card-tag-menu' : 'interaction-detail-tag-menu'"
      :model-value="interaction.tagIds"
      :brand-id="interaction.brandId"
      :dropdown="{
        targetNodes: `#${tagDropdownId}`,
        referenceNode: `#${tagDropdownId}`,
        placement: 'bottom-start',
      }"
      :apply-callback="onAssignTags"
    />
    <AssignmentMenu
      v-show="showAssigneeAvatar"
      :key="inPreview ? 'preview-card-assignment-menu' : 'interaction-detail-assignment-menu'"
      :class="[
        { '!ml-2 !mr-1': showAssigneeAvatar && !vertical },
        { '!mt-2': showAssigneeAvatar && vertical },
      ]"
      :show-avatar="showAssigneeAvatar"
      :model-value="interaction.assigneeId ?? -1"
      :brand-id="interaction.brandId"
      :dropdown="assignmentDropdown"
      :apply-callback="onAssignUser"
    />
    <MenuButtonGroup
      v-if="!collapsed && hiddenMenuItems.length"
      key="interactionHiddenMenuButtonGroup"
      :model="hiddenMenuItems"
      :display-limit="0"
    />
  </div>
</template>
